import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { X, RefreshCw, Package, ChevronDown, ChevronRight, MapPin, Check, Truck, CreditCard, ShoppingBag, PackageCheck, XCircle } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';
import { SYMBOLS } from '../context/CurrencyContext';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
  pending:   { label: 'Pending',   bg: 'bg-amber-100',   text: 'text-amber-700' },
  paid:      { label: 'Paid',      bg: 'bg-blue-100',    text: 'text-blue-700' },
  shipped:   { label: 'Shipped',   bg: 'bg-indigo-100',  text: 'text-indigo-700' },
  delivered: { label: 'Delivered', bg: 'bg-emerald-100', text: 'text-emerald-700' },
  cancelled: { label: 'Cancelled', bg: 'bg-neutral-200', text: 'text-neutral-600' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.pending;
  return <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11.5px] font-medium ${m.bg} ${m.text}`}>{m.label}</span>;
};

// Ordered milestones the customer sees. `paid` and `shipped` and `delivered`
// each check off from the order's `status_history` map. Cancellation replaces
// the whole timeline with a distinct cancelled state.
const TIMELINE_STEPS = [
  { key: 'pending',   label: 'Order placed',        Icon: ShoppingBag },
  { key: 'paid',      label: 'Payment confirmed',   Icon: CreditCard },
  { key: 'shipped',   label: 'Shipped',             Icon: Truck },
  { key: 'delivered', label: 'Delivered',           Icon: PackageCheck },
];

const STATUS_RANK = { pending: 0, paid: 1, shipped: 2, delivered: 3 };

const fmt = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

export const StatusTimeline = ({ order }) => {
  const status = order.status || 'pending';
  const history = order.status_history || {};

  if (status === 'cancelled') {
    return (
      <div data-testid={`timeline-${order.id}`} className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center text-neutral-500">
          <XCircle size={18} />
        </div>
        <div className="min-w-0">
          <div className="text-[13.5px] font-medium text-neutral-900 dark:text-neutral-100">Order cancelled</div>
          <div className="text-[11.5px] text-neutral-500">{fmt(history.cancelled) || 'This order was cancelled.'}</div>
        </div>
      </div>
    );
  }

  const currentRank = STATUS_RANK[status] ?? 0;

  return (
    <div data-testid={`timeline-${order.id}`} className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4">
      <ol className="grid grid-cols-4 gap-2 relative">
        {TIMELINE_STEPS.map((step, i) => {
          const rank = STATUS_RANK[step.key];
          const done = rank <= currentRank;
          const active = rank === currentRank;
          // Timestamp: prefer explicit history entry; fall back to order.at for 'pending'.
          const ts = history[step.key] || (step.key === 'pending' ? order.at : '');
          const { Icon } = step;
          return (
            <li key={step.key} data-testid={`timeline-${order.id}-${step.key}`} className="relative flex flex-col items-center text-center">
              {/* Connector line to next step */}
              {i < TIMELINE_STEPS.length - 1 && (
                <span
                  className={`absolute top-4 left-1/2 w-full h-[2px] ${done && STATUS_RANK[TIMELINE_STEPS[i + 1].key] <= currentRank ? 'bg-emerald-500' : 'bg-neutral-200 dark:bg-neutral-800'}`}
                  aria-hidden="true"
                />
              )}
              <div className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center border-2 ${done ? 'bg-emerald-500 border-emerald-500 text-white' : active ? 'bg-white dark:bg-[#141416] border-neutral-400 text-neutral-400' : 'bg-white dark:bg-[#141416] border-neutral-200 dark:border-neutral-700 text-neutral-300'}`}>
                {done ? <Check size={16} /> : <Icon size={15} />}
              </div>
              <div className={`mt-2 text-[11.5px] font-medium ${done ? 'text-neutral-900 dark:text-neutral-100' : 'text-neutral-500'}`}>{step.label}</div>
              {ts && done && (
                <div className="text-[10.5px] text-neutral-500 mt-0.5 leading-tight">{fmt(ts)}</div>
              )}
            </li>
          );
        })}
      </ol>
      {(order.shipments?.length ? order.shipments : order.tracking_number ? [{ id: 'legacy', tracking_number: order.tracking_number, carrier: order.carrier }] : []).map((s, i, all) => (
        <div key={s.id} data-testid={`customer-shipment-${s.id}`} className={`${i === 0 ? 'mt-3 pt-3 border-t border-neutral-100 dark:border-neutral-800' : 'mt-1.5'} flex flex-wrap items-center gap-2 text-[12px] text-neutral-700 dark:text-neutral-300`}>
          <Truck size={13} className="text-emerald-600" />
          <span>{all.length > 1 ? `Package ${i + 1}:` : 'Tracking:'}</span>
          <span className="font-mono">{s.tracking_number}</span>
          {s.carrier && <span className="text-neutral-500">· {s.carrier}</span>}
          {s.note && <span className="text-neutral-500">— {s.note}</span>}
        </div>
      ))}
    </div>
  );
};

const OrderHistoryModal = ({ open, onClose }) => {
  const { headers, auth } = useCustomerAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!auth?.token) return;
    setLoading(true); setErr('');
    try {
      const { data } = await axios.get(`${API}/my-orders`, { headers });
      setOrders(data || []);
    } catch (e) {
      setErr('Failed to load your orders. Please try again.');
    } finally { setLoading(false); }
  }, [auth?.token, headers]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[65] bg-black/40 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="relative bg-white dark:bg-[#141416] rounded-2xl w-full max-w-3xl my-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200 dark:border-neutral-800">
          <div>
            <h3 className="font-serif text-[22px] text-neutral-900 dark:text-neutral-100">Order history</h3>
            <p className="text-[12px] text-neutral-500 mt-0.5">Signed in as {auth?.email}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-1.5 border border-neutral-200 dark:border-neutral-700 rounded-md px-3 py-1.5 text-[12.5px] hover:bg-neutral-50 dark:hover:bg-neutral-800"><RefreshCw size={13} /> Refresh</button>
            <button onClick={onClose} className="w-8 h-8 rounded-full border border-neutral-200 dark:border-neutral-700 flex items-center justify-center hover:bg-neutral-50 dark:hover:bg-neutral-800"><X size={15} /></button>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto">
          {loading && orders.length === 0 && (
            <div className="py-16 text-center text-neutral-500 text-[13.5px]">Loading orders…</div>
          )}
          {err && <div className="px-6 py-4 text-red-600 text-[13px]">{err}</div>}
          {!loading && orders.length === 0 && !err && (
            <div className="py-16 text-center text-neutral-500">
              <Package size={36} className="mx-auto mb-3 opacity-40" />
              <p className="text-[14px]">You haven't placed any orders yet.</p>
              <p className="text-[12px] mt-1">Once you complete a checkout, your order will appear here.</p>
            </div>
          )}

          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {orders.map(o => {
              const L = o.currency && o.currency !== 'USD' && o.local_totals ? o.local_totals : null;
              const sym = L ? (SYMBOLS[o.currency] || '') : '$';
              const m = (local, usd) => `${sym}${Number(L ? local : usd || 0).toFixed(2)}`;
              const isOpen = !!expanded[o.id];
              const f = o.form || {};
              const items = o.items || [];
              return (
                <div key={o.id}>
                  <button onClick={() => toggle(o.id)} className="w-full grid grid-cols-[20px_1.4fr_1.2fr_0.8fr_0.8fr] gap-3 items-center px-6 py-4 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900">
                    <div className="text-neutral-400">{isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</div>
                    <div>
                      <div className="font-mono text-[13px] text-neutral-900 dark:text-neutral-100">{o.id}</div>
                      <div className="text-[11.5px] text-neutral-500 mt-0.5">{o.at ? new Date(o.at).toLocaleString() : ''}</div>
                    </div>
                    <div className="text-[13px] text-neutral-700 dark:text-neutral-300 truncate">{items.length} item{items.length !== 1 ? 's' : ''} · {o.network || '—'}</div>
                    <div className="text-[14px] font-medium text-neutral-900 dark:text-neutral-100">{m(L?.total, o.total)}</div>
                    <div><StatusBadge status={o.status || 'pending'} /></div>
                  </button>

                  {isOpen && (
                    <div className="px-6 pb-5 space-y-4 bg-neutral-50/60 dark:bg-neutral-900/40">
                      <StatusTimeline order={o} />
                      <Link to={`/order/${o.id}`} onClick={onClose} data-testid={`manage-order-${o.id}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-brand/30 bg-emerald-50/60 dark:bg-emerald-950/20 px-4 py-3 text-[13px] hover:border-brand">
                        <span className="text-neutral-700 dark:text-neutral-200">Upload payment proof, request changes or message us</span>
                        <span className="font-semibold text-brand whitespace-nowrap">Manage order →{o.unread_customer_msgs ? ` (${o.unread_customer_msgs} new)` : ''}</span>
                      </Link>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <h4 className="text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Shipping</h4>
                        <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] p-4 text-[13.5px] space-y-1.5">
                          <div className="text-neutral-900 dark:text-neutral-100">{f.fullName || '—'}</div>
                          <div className="text-neutral-600 dark:text-neutral-300 flex gap-2"><MapPin size={13} className="mt-0.5 text-neutral-400" />
                            <div>{[f.address, f.apt].filter(Boolean).join(', ')}<br />{[f.city, f.state, f.postalCode].filter(Boolean).join(', ')}<br />{f.country}</div>
                          </div>
                          {f.telegram && <div className="text-neutral-500 text-[12px]">Telegram: {f.telegram}</div>}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-[11px] tracking-widest uppercase text-neutral-500 mb-2">Items</h4>
                        <div className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-[#141416] divide-y divide-neutral-100 dark:divide-neutral-800">
                          {items.map((it, i) => (
                            <div key={i} className="flex items-start justify-between gap-3 p-3">
                              <div className="min-w-0">
                                <div className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100 truncate">{it.name}</div>
                                <div className="text-[11.5px] text-neutral-500">{it.strength}{it.packaging ? ' · ' + it.packaging : ''} • Qty {it.qty}</div>
                              </div>
                              <div className="text-[13px] font-medium text-neutral-900 dark:text-neutral-100 whitespace-nowrap">{m((it.price_local ?? 0) * Number(it.qty), Number(it.price) * Number(it.qty))}</div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-3 border border-neutral-200 dark:border-neutral-800 rounded-lg p-3 text-[13px] bg-white dark:bg-[#141416] space-y-1">
                          <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span>{m(L?.subtotal, o.subtotal)}</span></div>
                          {Number(o.discountAmount) > 0 && (
                            <div className="flex justify-between text-emerald-600"><span>Discount{o.coupon_code ? ` (${o.coupon_code})` : ''}</span><span>- {m(L?.discount, o.discountAmount)}</span></div>
                          )}
                          <div className="flex justify-between"><span className="text-neutral-500">Shipping</span><span>{m(L?.shipping, o.shipping)}</span></div>
                          <div className="flex justify-between font-semibold pt-1 border-t border-neutral-100 dark:border-neutral-800"><span>Total</span><span>{m(L?.total, o.total)}{L && <span className="block text-[11px] font-normal text-neutral-500 text-right">≈ ${Number(o.total).toFixed(2)} USD</span>}</span></div>
                        </div>
                      </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderHistoryModal;
